/*
  GEMDRAW — Announcement marquee

  Keeps the strip filled and moving at one constant speed.

  The section renders several copies of the message. This element measures one
  copy, clones more if the strip is still narrower than twice the viewport, and
  drives the loop by shifting exactly one copy width so the repetition is
  seamless at any width.

  SPEED. The merchant sets how many seconds a message should take to cross the
  screen. That is not the animation duration: the loop shifts by one copy width,
  so at a fixed duration a longer message would travel faster and a phone would
  be slower than a desktop. The per-copy duration is solved for instead —

    duration = crossSeconds * copyWidth / viewportWidth

  — which holds the pixel speed constant at any message length and any viewport,
  and is re-solved on resize.

  With reduced motion the element does nothing at all: the stylesheet shows a
  single static, centered message instead. Pausing on hover and focus is also
  handled in CSS, so the strip keeps behaving correctly if this script fails.
*/

(function () {
  'use strict';

  const MIN_VIEWPORT_MULTIPLE = 2;
  const MAX_CLONES = 40;

  class GemdrawAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gd-announcement-track]');
      if (!this.track) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onResize = this.debounce(this.layout.bind(this), 150);

      /*
        Measuring before the webfont has swapped in produces a copy width from
        the fallback face, and the loop then shifts by the wrong distance — the
        strip visibly jumps and the first message ends up clipped. Waiting for
        the font means the first frame starts at rest, fully readable, and the
        loop is seamless from the very first cycle.
      */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.layout.bind(this));
      } else {
        this.layout();
      }

      window.addEventListener('resize', this.onResize);
      this.reducedMotion.addEventListener('change', this.onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      if (this.reducedMotion) this.reducedMotion.removeEventListener('change', this.onResize);
    }

    layout() {
      if (!this.track) return;

      /* The static composition is complete on its own — leave it alone. */
      if (this.reducedMotion.matches) {
        this.removeAttribute('data-gd-ready');
        return;
      }

      /* Re-measuring must start from the server-rendered copies, otherwise a
         resize would keep stacking clones on top of the previous run. */
      this.track.querySelectorAll('[data-gd-clone]').forEach(function (node) {
        node.remove();
      });

      const original = this.track.querySelector('.gd-announcement__item');
      if (!original) return;

      const itemWidth = original.getBoundingClientRect().width;
      if (!itemWidth) return;

      const target = window.innerWidth * MIN_VIEWPORT_MULTIPLE + itemWidth;
      let guard = 0;

      while (this.track.getBoundingClientRect().width < target && guard < MAX_CLONES) {
        const clone = original.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-gd-clone', 'true');
        clone.querySelectorAll('a').forEach(function (link) {
          link.setAttribute('tabindex', '-1');
        });
        this.track.appendChild(clone);
        guard += 1;
      }

      /*
        Solve for the per-copy duration that makes a message cross the viewport
        in the requested time. Clamped at both ends so a very short message on a
        very wide screen cannot resolve to a duration fast enough to read as a
        scroll rather than a drift.
      */
      const cross = parseFloat(this.dataset.cross) || 32;
      const viewport = window.innerWidth || itemWidth;
      const duration = Math.max(3, Math.min(30, (cross * itemWidth) / viewport));

      this.style.setProperty('--gd-marquee-shift', '-' + itemWidth.toFixed(2) + 'px');
      this.style.setProperty('--gd-marquee-duration', duration.toFixed(2) + 's');
      this.setAttribute('data-gd-ready', 'true');
    }

    debounce(fn, wait) {
      let timer = null;
      return function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(fn, wait);
      };
    }
  }

  if (!customElements.get('gd-announcement-bar')) {
    customElements.define('gd-announcement-bar', GemdrawAnnouncementBar);
  }
})();
